import { useState, useCallback } from "react";
import {
  QrCode, Copy, CheckCircle, Smartphone, Wifi, Share2,
  ExternalLink, Shield, ArrowRight, Monitor,
} from "lucide-react";

const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || window.location.origin;
const SFR_URL = `${window.location.origin}/my-sectors`;

const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(SFR_URL)}&size=240x240&color=60a5fa&bgcolor=0f172a&margin=2`;

function Step({ num, title, desc, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-xs font-black text-primary">{num}</span>
      </div>
      <div>
        <div className="text-sm font-bold text-foreground flex items-center gap-1.5">
          <Icon size={13} className="text-muted-foreground" /> {title}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

export default function PilotAccess() {
  const [copied, setCopied] = useState(false);
  const [msgCopied, setMsgCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(SFR_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  }, []);

  const shareMsg = `Hi,\n\nPlease install the Fatigue Shield crew app on your phone:\n\n${SFR_URL}\n\nOpen the link → tap "Add to Home Screen" → done. Use this to rate your sectors after each flight (takes 30 seconds).\n\nFly safe.`;

  const handleCopyMsg = () => {
    navigator.clipboard.writeText(shareMsg).then(() => {
      setMsgCopied(true);
      setTimeout(() => setMsgCopied(false), 3000);
    });
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
  const emailUrl   = `mailto:?subject=Fatigue Shield — Install the Crew App&body=${encodeURIComponent(shareMsg)}`;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="pilot-access-page">
      <div>
        <h1 className="text-2xl font-black text-foreground tracking-tight">Pilot App Access</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Share the SFR Sector Rating app with your pilot roster. No App Store required — one scan, installed.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code */}
        <div className="bg-card border border-border rounded-sm p-6 flex flex-col items-center text-center space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground">
            <QrCode size={13} /> Scan to Install
          </div>
          <div className="p-3 bg-[#0f172a] rounded-sm border border-primary/20" data-testid="qr-code-container">
            <img src={QR_URL} alt="QR Code for Fatigue Shield pilot app" width={240} height={240}
              className="rounded-sm" onError={e => e.target.style.display="none"} />
          </div>
          <div className="space-y-1">
            <div className="text-xs font-bold text-foreground">Display in crew briefing room / crew room</div>
            <div className="text-[10px] text-muted-foreground">Pilot scans → browser opens → "Add to Home Screen" → installed</div>
          </div>
          <div className="w-full font-mono text-xs text-primary bg-primary/5 border border-primary/20 rounded-sm px-3 py-2 text-center break-all">
            {SFR_URL}
          </div>
          <button onClick={handleCopy}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground text-sm font-bold rounded-sm hover:bg-primary/90 transition-colors"
            data-testid="copy-link-btn">
            {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>

        {/* Installation steps */}
        <div className="space-y-5">
          {/* Android */}
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Smartphone size={14} className="text-green-400" />
              <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Android / Chrome</div>
            </div>
            <div className="space-y-3">
              <Step num="1" icon={Wifi} title="Open Chrome on phone" desc={`Navigate to: ${SFR_URL}`} />
              <Step num="2" icon={Monitor} title="Tap ⋮ menu" desc='Select "Add to Home screen"' />
              <Step num="3" icon={Shield} title="Confirm install" desc='Tap "Add" — icon appears on home screen' />
              <Step num="4" icon={ArrowRight} title="Open app, log in" desc="Rate sectors. Done." />
            </div>
          </div>

          {/* iOS */}
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Smartphone size={14} className="text-blue-400" />
              <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">iPhone / Safari</div>
            </div>
            <div className="space-y-3">
              <Step num="1" icon={Wifi} title="Open Safari on iPhone" desc={`Navigate to: ${SFR_URL}`} />
              <Step num="2" icon={Share2} title='Tap the Share icon (□↑)' desc='Bottom centre of Safari toolbar' />
              <Step num="3" icon={Monitor} title='"Add to Home Screen"' desc='Scroll down in share sheet → tap "Add to Home Screen"' />
              <Step num="4" icon={CheckCircle} title="Confirm name → Add" desc="App icon appears on home screen instantly" />
            </div>
          </div>
        </div>
      </div>

      {/* Share message */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Share2 size={13} className="text-muted-foreground" />
            <div className="text-xs font-black uppercase tracking-widest text-muted-foreground">Ready-to-Send Message</div>
          </div>
          <div className="flex items-center gap-2">
            <a href={whatsappUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold rounded-sm hover:bg-green-500/20 transition-colors"
              data-testid="whatsapp-share-btn">
              <ExternalLink size={10} /> WhatsApp
            </a>
            <a href={emailUrl}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold rounded-sm hover:bg-blue-500/20 transition-colors"
              data-testid="email-share-btn">
              <ExternalLink size={10} /> Email
            </a>
            <button onClick={handleCopyMsg}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-border text-xs text-muted-foreground hover:text-foreground rounded-sm transition-colors"
              data-testid="copy-msg-btn">
              {msgCopied ? <CheckCircle size={10} className="text-green-400" /> : <Copy size={10} />}
              {msgCopied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
        <pre className="text-xs text-muted-foreground bg-background/60 border border-border rounded-sm p-3 whitespace-pre-wrap leading-relaxed font-sans">
          {shareMsg}
        </pre>
      </div>

      <div className="text-[10px] text-muted-foreground text-center">
        All SFR submissions are confidential. High-stress ratings (≥ 5) are flagged to the safety team.
        Powered by Fatigue Shield · ICAO Doc 9966 aligned.
      </div>
    </div>
  );
}
